import { useCallback, useState } from "react";
import { useStructuredCompletion } from "../ai/useAiCompletion";

export type MarkdownSearchReplaceChange = {
  search: string;
  replace: string;
};

const MARKDOWN_EDITS_SCHEMA = JSON.stringify({
  type: "object",
  additionalProperties: false,
  properties: {
    changes: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        properties: {
          search: {
            type: "string",
            description:
              "Exact placeholder substring copied verbatim from the document; all occurrences are replaced",
          },
          replace: { type: "string", description: "Replacement value from the fill plan" },
        },
        required: ["search", "replace"],
      },
    },
  },
  required: ["changes"],
});

const APPLY_EDITS_SYSTEM_PROMPT = `You apply a fill plan to a contract document (Markdown) for Norwegian public procurement.

You receive the full current Markdown document and a fill plan describing which placeholders to replace with which values.

Return JSON only matching the enforced schema: an object with a "changes" array.
Each change has:
- search: the EXACT placeholder substring copied verbatim from the document
- replace: the replacement value from the fill plan

Rules:
- Use ONLY searchReplace-style edits: each change replaces every occurrence of "search" with "replace".
- The "search" string must be copied character-for-character from the document. Do not normalize whitespace, HTML entities, or markdown syntax.
- Prefer short, distinctive placeholder strings such as bracketed fields: [HE firmanavn], [UE firmanavn], [Fyll ut], [Beløp], [dd.mm.yyyy], etc.
- Do NOT use long anchors or surrounding table cells as the search string.
- For each item in the fill plan, create one change that replaces the corresponding placeholder with the specified value.
- If the same placeholder text should be replaced everywhere with the same value, use a single change with that exact placeholder as "search".
- If a placeholder cannot be located in the document without guessing, skip it.
- If no changes can be applied, return an empty changes array.`;

const buildApplyEditsMessages = (documentMarkdown: string, planMarkdown: string) => [
  {
    role: "SYSTEM" as const,
    content: APPLY_EDITS_SYSTEM_PROMPT,
  },
  {
    role: "USER" as const,
    content: [`## Original document\n${documentMarkdown}`, `## Fill plan\n${planMarkdown}`].join("\n\n"),
  },
];

const parseMarkdownEdits = (json: string): MarkdownSearchReplaceChange[] | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }

  const changes = (parsed as { changes?: unknown })?.changes;
  if (!Array.isArray(changes)) return null;

  const validChanges: MarkdownSearchReplaceChange[] = [];
  for (const item of changes) {
    if (typeof item !== "object" || item === null) continue;
    const search = (item as { search?: unknown }).search;
    const replace = (item as { replace?: unknown }).replace;
    if (typeof search !== "string" || typeof replace !== "string") continue;
    validChanges.push({ search, replace });
  }

  return validChanges;
};

export const applyMarkdownSearchReplaceChanges = (markdown: string, changes: MarkdownSearchReplaceChange[]): string => {
  let result = markdown;
  for (const { search, replace } of changes) {
    if (!search || !result.includes(search)) continue;
    result = result.replaceAll(search, replace);
  }
  return result;
};

export const useMarkdownEditing = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const runStructured = useStructuredCompletion({ feature: "markdown-editing-v2" });

  const applyEdits = useCallback(
    async (documentMarkdown: string, planMarkdown: string): Promise<string | null> => {
      const trimmedPlan = planMarkdown.trim();
      if (!documentMarkdown.trim() || !trimmedPlan) {
        setEditError("Document and fill plan must be non-empty.");
        return null;
      }

      setIsEditing(true);
      setEditError(null);

      try {
        const json = await runStructured({
          messages: buildApplyEditsMessages(documentMarkdown, trimmedPlan),
          jsonSchema: MARKDOWN_EDITS_SCHEMA,
        });

        const changes = parseMarkdownEdits(json);
        if (changes === null) {
          setEditError("Failed to parse JSON from model.");
          return null;
        }

        return applyMarkdownSearchReplaceChanges(documentMarkdown, changes);
      } catch (err) {
        setEditError(err instanceof Error ? err.message : "Edit request failed.");
        return null;
      } finally {
        setIsEditing(false);
      }
    },
    [runStructured]
  );

  const clearEditError = useCallback(() => setEditError(null), []);

  return {
    applyEdits,
    isEditing,
    editError,
    clearEditError,
  };
};
